import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {ProductTrackingData} from '@aboutyou/tracking';
import React, {ComponentType, FC, useMemo, useState} from 'react';
import {ProductMiniAdp} from '../../../features/ProductMiniAdp/ProductMiniAdp';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {AddToBasketPayload} from '../../../features/ProductTile/types';
import {AddToBasketFlyout} from '../../../features/SizeSelectionFlyout/AddToBasketFlyout';
import {AddToBasketOnSuccessProps} from '../../../features/SizeSelectionFlyout/hooks/useAddToBasket';
import {SizeSelectionOpenedStateKey} from '../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {ComplementaryRecommendationProps} from '../ComplementaryRecommendation/ComplementaryRecommendation';
import {RecommendationsProp} from '../Recommendations/Recommendations';
import {useDeliveriesSection} from '../hooks/useGetBasketPageSection';

export const RecommendationWrapper: FC<{
  addToBasketMethod?: 'flyout' | 'miniAdp';
  smallerPaddings?: boolean;
  showNavigation?: boolean;
  isSmallFormat?: boolean;
  enableGridLayout?: boolean;
  ViewComponent: ComponentType<
    ComplementaryRecommendationProps | RecommendationsProp
  >;
  name?: string;
  onAddToBasketSuccess?: (props: AddToBasketOnSuccessProps) => void;
  ProductTileComponent?: React.FC<ProductTileProps>;
  addToCTALabel?: string;
}> = ({
  addToBasketMethod,
  smallerPaddings,
  ViewComponent,
  name,
  onAddToBasketSuccess,
  ProductTileComponent,
  addToCTALabel,
}) => {
  const {isOpen, cancel, open} = useOpenedStateManager(
    `A2B_FROM_RECOLANE_${name || addToBasketMethod}`,
  );

  const {refetch: refetchBasketSection} = useDeliveriesSection();

  const [selectedSize, setSelectedSize] = useState<
    SizeSelection_Size | undefined
  >(undefined);
  const [selectedProductTile, setSelectedProductTile] = useState<
    AddToBasketPayload | undefined
  >(undefined);
  const [selectedTrackingData, setSelectedTrackingData] = useState<
    ProductTrackingData | undefined
  >(undefined);

  const onClickAddToBasket = (productTile: AddToBasketPayload | undefined) => {
    setSelectedSize(undefined);
    setSelectedProductTile(productTile);
    setSelectedTrackingData(productTile?.trackingData);
    return open();
  };

  const handleScrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  const onA2BSuccessHandler = async ({
    freeDeliverySection,
    statusSuccessSections,
  }: {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    setSelectedProductTile(undefined);
    await refetchBasketSection();
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
    handleScrollToTop();
  };

  const memoTrackingData = useMemo(() => {
    return selectedTrackingData
      ? {...selectedTrackingData, brandName: ''}
      : undefined;
  }, [selectedTrackingData]);

  return (
    <>
      <ViewComponent
        onClickAddToBasket={onClickAddToBasket}
        smallerPaddings={smallerPaddings}
        ProductTileComponent={ProductTileComponent}
        addToCTALabel={addToCTALabel}
      />

      {!!selectedProductTile && addToBasketMethod === 'flyout' && (
        <AddToBasketFlyout
          isOpen={isOpen}
          product={selectedProductTile.product}
          withSuccessToast
          withErrorToast
          onSuccess={onA2BSuccessHandler}
          onError={handleScrollToTop}
          onClose={() => cancel()}
          onCaptchaClose={() => {
            setSelectedProductTile(undefined);
          }}
          trackingData={memoTrackingData}
          badgesListKeys={selectedProductTile.badgesListKeys ?? []}
          couponPromotionId={selectedProductTile.couponPromotionId}
        />
      )}
      {!!selectedProductTile && addToBasketMethod === 'miniAdp' && (
        <ProductMiniAdp
          close={cancel}
          isOpen={isOpen}
          productId={selectedProductTile.product.id}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          onSuccess={onA2BSuccessHandler}
          sizeSelectionStateManagerKey={
            SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
          }
          showToastMessage
          trackingData={selectedTrackingData}
          couponPromotionId={selectedProductTile.couponPromotionId}
        />
      )}
    </>
  );
};
